import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";

// Responsive: dense table on desktop (lg+), touch-friendly card list on mobile.
// Pages pass the same `columns`; the first column is used as the card title and the
// rest become label/value rows on mobile. No page changes required.
export function DataTable({ columns, rows, onRowClick, empty, testid, rowTestId, mobileCard }) {
  const emptyMsg = empty || "No records";
  const [title, ...rest] = columns;

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {columns.map((c) => (
                  <th key={c.key} className={cn("text-[11px] font-semibold uppercase tracking-wider text-slate-500 py-2.5 px-3 whitespace-nowrap", c.align === "right" && "text-right")}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={columns.length} className="py-10 text-center text-sm text-slate-400">{emptyMsg}</td></tr>
              )}
              {rows.map((row, i) => (
                <tr key={row.id || i} onClick={onRowClick ? () => onRowClick(row) : undefined} data-testid={rowTestId ? `${rowTestId}-${row.id || i}` : undefined}
                  className={cn("border-b border-slate-100 last:border-0 text-sm text-slate-800 transition-colors", onRowClick && "hover:bg-slate-50 cursor-pointer")}>
                  {columns.map((c) => (
                    <td key={c.key} className={cn("py-2.5 px-3 align-middle", c.align === "right" && "text-right", c.mono && "font-mono tabular")}>{c.render ? c.render(row) : row[c.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="lg:hidden space-y-2.5" data-testid={testid ? `${testid}-cards` : undefined}>
        {rows.length === 0 && <div className="bg-white border border-slate-200 rounded-xl py-10 text-center text-sm text-slate-400">{emptyMsg}</div>}
        {rows.map((row, i) => {
          const inner = mobileCard ? mobileCard(row) : (
            <>
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1 font-medium text-slate-900 text-[15px]">{title.render ? title.render(row) : row[title.key]}</div>
                {onRowClick && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
              </div>
              {rest.length > 0 && (
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {rest.map((c) => (
                    <div key={c.key} className={cn("min-w-0", c.align === "right" && "text-right")}>
                      <dt className="text-[10px] uppercase tracking-wider text-slate-400">{c.header}</dt>
                      <dd className={cn("text-sm text-slate-700 truncate", c.mono && "font-mono tabular")}>{c.render ? c.render(row) : (row[c.key] ?? "—")}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </>
          );
          return (
            <div key={row.id || i} onClick={onRowClick ? () => onRowClick(row) : undefined} data-testid={rowTestId ? `${rowTestId}-${row.id || i}` : undefined}
              className={cn("bg-white border border-slate-200 rounded-xl p-3.5", onRowClick && "cursor-pointer active:bg-slate-50 hover:border-slate-300 transition-colors")} role={onRowClick ? "button" : undefined}>
              {inner}
            </div>
          );
        })}
      </div>
    </>
  );
}
